import { useParams, Link, Navigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { CheckCircle, ShieldCheck, Clock, Truck } from "lucide-react";
import Layout from "../Layout";
import SEOHead from "../SEOHead";
import Breadcrumb from "../Breadcrumb";
import FAQSection from "../FAQSection";
import { getIndustryBySlug, industries } from "../../data/industries";
import { business } from "../../config/business";

const IndustryPage = () => {
  const { slug } = useParams();
  const industry = getIndustryBySlug(slug);

  if (!industry) return <Navigate to="/industries" replace />;

  const related = industries.filter((i) => i.slug !== industry.slug).slice(0, 3);

  const serviceJsonLd = {
    "@context": "https://schema.org",
    "@type": "Service",
    serviceType: `PEB structures for ${industry.name}`,
    provider: { "@type": "Organization", name: business.name },
    description: industry.intro,
  };

  return (
    <Layout>
      <SEOHead
        title={`PEB for ${industry.name} | Vasavi Structures`}
        description={`${industry.tagline} PEB design and fabrication by Vasavi Structures, Hyderabad.`}
        keywords={`PEB for ${industry.name}, industrial shed ${industry.name}, ${industry.name} structure manufacturer`}
      />
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(serviceJsonLd)}</script>
      </Helmet>
      <Breadcrumb items={[{ name: "Industries", path: "/industries" }, { name: industry.name }]} />

      {/* 1. Hero */}
      <section className="section-padding pb-10">
        <div className="container-custom">
          <h1 className="font-montserrat text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-4 max-w-3xl">
            PEB Structures for <span className="text-secondary">{industry.name}</span>
          </h1>
          <p className="text-lg text-secondary font-montserrat font-semibold mb-4">{industry.tagline}</p>
          <p className="text-muted-foreground max-w-3xl text-lg">{industry.intro}</p>
        </div>
      </section>

      {/* 2. Trust strip */}
      <section className="bg-primary py-6">
        <div className="container-custom">
          <div className="flex flex-wrap items-center justify-center gap-x-10 gap-y-3 text-primary-foreground text-center">
            <span className="font-montserrat font-semibold flex items-center gap-2"><ShieldCheck size={18} aria-hidden="true" /> Requirement-specific design</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Clock size={18} aria-hidden="true" /> Fast fabrication & erection</span>
            <span className="font-montserrat font-semibold flex items-center gap-2"><Truck size={18} aria-hidden="true" /> Telangana & AP delivery</span>
          </div>
        </div>
      </section>

      {/* 3. Requirements grid */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-foreground mb-10 text-center">
            What {industry.name} Structures Need
          </h2>
          <div className="grid sm:grid-cols-2 gap-6">
            {industry.requirements.map((req) => (
              <div key={req.title} className="flex gap-4 p-5 bg-card rounded-xl border border-border shadow-sm">
                <CheckCircle className="text-secondary flex-shrink-0 mt-1" size={22} aria-hidden="true" />
                <div>
                  <h3 className="font-montserrat font-bold text-foreground mb-1">{req.title}</h3>
                  <p className="text-sm text-muted-foreground">{req.text}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 4. Related services */}
      <section className="section-padding bg-muted">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6 text-center">
            Relevant Services
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { name: "PEB Buildings", path: "/pre-fab-structure-buildings" },
              { name: "Industrial Sheds", path: "/industrial-sheds" },
              { name: "Steel Fabrication", path: "/structure-steel-fabrication" },
              { name: "Design & Engineering", path: "/design-engineering" },
            ].map((service) => (
              <Link
                key={service.path}
                to={service.path}
                className="p-4 bg-card rounded-lg text-center font-montserrat font-semibold text-foreground hover:bg-primary hover:text-primary-foreground transition shadow-sm"
              >
                {service.name}
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 5. Related industries */}
      <section className="section-padding">
        <div className="container-custom">
          <h2 className="font-montserrat text-2xl font-bold text-foreground mb-6">Other Industries We Serve</h2>
          <div className="grid sm:grid-cols-3 gap-6">
            {related.map((r) => (
              <Link
                key={r.slug}
                to={`/industries/${r.slug}`}
                className="p-5 bg-card rounded-xl shadow-md card-hover border border-border"
              >
                <h3 className="font-montserrat font-bold text-foreground mb-1">{r.name}</h3>
                <p className="text-sm text-muted-foreground">{r.tagline}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* 6. FAQ */}
      <FAQSection faqs={industry.faqs} title={`${industry.name} — Frequently Asked Questions`} />

      {/* 7. CTA */}
      <section className="section-padding bg-secondary">
        <div className="container-custom text-center">
          <h2 className="font-montserrat text-2xl md:text-3xl font-bold text-secondary-foreground mb-4">
            Discuss Your {industry.name} Project
          </h2>
          <p className="text-secondary-foreground/90 mb-8 max-w-xl mx-auto">
            Share your requirements and our engineering team will work through the right design approach.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/enquiry" className="btn-primary inline-block px-8 py-4">
              Get Project Estimate
            </Link>
            <a href={business.whatsapp.href} target="_blank" rel="noopener noreferrer" className="btn-outline border-secondary-foreground text-secondary-foreground hover:bg-secondary-foreground hover:text-secondary inline-block px-8 py-4">
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default IndustryPage;
